import React from "react";
import { Redirect } from "react-router-dom";

import { FeatureToggleKey } from "@config/featureToggles";
import { ROUTES } from "@config/app.routing";

import { LOADING_STATE } from "@typings/reduxThunkActions";

import { useFeatureToggleManager } from "@hooks/useFeatureToggle";

interface Props {
  featureToggle: FeatureToggleKey | FeatureToggleKey[];
  children: React.ReactNode;
  redirect?: string;
  redirectWithReload?: boolean;
}

const FeatureToggleGuard = ({
  redirect = ROUTES.NOT_FOUND,
  ...props
}: Props): React.ReactElement => {
  const featureToggles = new Array(props.featureToggle).flat().filter(Boolean);
  const { status, config } = useFeatureToggleManager();

  const [redirectUrl, setRedirectUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (
      typeof props.featureToggle === "undefined" ||
      featureToggles.length === 0 ||
      status === LOADING_STATE.FAILED
    ) {
      setRedirectUrl(redirect);

      return;
    }

    if (
      status === LOADING_STATE.SUCCEEDED &&
      featureToggles.map(key => config[key]).some(value => !value)
    ) {
      setRedirectUrl(redirect);

      return;
    }
  }, [
    redirect,
    status,
    JSON.stringify(featureToggles),
    JSON.stringify(config),
  ]);

  if (!!redirectUrl && props.redirectWithReload) {
    window.location.href = redirect;
  }

  return (
    <React.Fragment>
      {!!redirectUrl && !props.redirectWithReload && <Redirect to={redirect} />}

      {props.children}
    </React.Fragment>
  );
};

export { FeatureToggleGuard };
